import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Shared modal frame — backdrop + dialog + headerless body. Adopted
 * by the hand-rolled modals (AddNoteModal, RaiseFlagModal,
 * AddDocumentModal) so their accent + error treatments unify under
 * the Aurora tokens.
 *
 * ConfirmModal uses its own inline shell because its header (icon +
 * title in a tone-coloured strip) is structurally different. This
 * primitive is the right pick when the modal content owns its own
 * header / form structure.
 *
 * Provides:
 *   - Backdrop click → onClose
 *   - Escape key → onClose
 *   - body scroll lock while open
 *   - aria-modal + labelledby plumbing (caller supplies titleId)
 *
 * Layout: full-bleed backdrop, bottom-sheet on mobile, centred card
 * on sm+. Width controlled by `maxWidth` (default `max-w-md`).
 */

export interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  /** id of the visible title — caller assigns matching id on its h2. */
  titleId: string;
  /** Card width class. Default 'max-w-md'. */
  maxWidth?: string;
  /** Disable Escape + backdrop close while a submit is in flight. */
  busy?: boolean;
  /** Show the corner close button (default true). */
  showClose?: boolean;
  /** Override the default rounded-token-lg / bg-surface shell. Use
   * sparingly — e.g. if a modal needs a tone-coloured header strip. */
  className?: string;
  children: ReactNode;
}

export function ModalShell({
  open,
  onClose,
  titleId,
  maxWidth = 'max-w-md',
  busy = false,
  showClose = true,
  className = '',
  children,
}: ModalShellProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, busy]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-end justify-center bg-fg/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        disabled={busy}
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div
        className={[
          // p-5 so content never sits flush to the card edge; a slightly
          // stronger hairline + shadow reads clearly as a floating dialog.
          'aurora-fade-in relative flex w-full flex-col overflow-hidden rounded-token-lg border border-line-strong/15 bg-surface p-5 shadow-token-lg',
          maxWidth,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {showClose ? (
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
        {children}
      </div>
    </div>
  );
}
